// src/components/cognition/cognition-model-modal.tsx
import React from "react";
import { CheckCircle2, Circle, Check, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

type AIModel = {
  id: string;
  name: string;
  provider: string;
  description?: string;
  recommended: boolean;
  isOptional?: boolean;
};

export function CognitionModelModal({
  open,
  onOpenChange,
  aiModels,
  selectedModels,
  onToggleModel,
  onUseRecommended,
  onSelectAll,
  onClearAll,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  aiModels: AIModel[];
  selectedModels: string[];
  onToggleModel: (id: string) => void;
  onUseRecommended: () => void;
  onSelectAll: () => void;
  onClearAll: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
        <DialogHeader className="p-6" style={{ borderBottom: "1px solid var(--border)" }}>
          <div>
            <DialogTitle>Choose AI Models</DialogTitle>
            <DialogDescription>Select which AI models to include in your cognition analysis</DialogDescription>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={onUseRecommended}
              className="px-4 py-2 rounded-lg border transition-all flex items-center gap-2"
              style={{
                backgroundColor: "var(--spectral-violet-8)",
                borderColor: "var(--spectral-violet-30)",
                color: "var(--spectral-violet)",
              }}
            >
              <Sparkles className="w-4 h-4" />
              <span>Use Recommended</span>
            </button>

            <button
              onClick={onSelectAll}
              className="px-3 py-1.5 rounded-lg border transition-colors"
              style={{ borderColor: "var(--border)", backgroundColor: "var(--muted)", color: "var(--foreground)" }}
            >
              Select All
            </button>

            <button
              onClick={onClearAll}
              className="px-3 py-1.5 rounded-lg border transition-colors"
              style={{ borderColor: "var(--border)", backgroundColor: "var(--muted)", color: "var(--foreground)" }}
            >
              Clear All
            </button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-2">
            {aiModels.map((model) => {
              const isSelected = selectedModels.includes(model.id);

              return (
                <button
                  key={model.id}
                  onClick={() => onToggleModel(model.id)}
                  className="w-full text-left p-4 rounded-lg border transition-all"
                  style={{
                    backgroundColor: isSelected ? "var(--alpha-teal-2)" : "var(--card)",
                    borderColor: isSelected ? "var(--accent)" : "var(--border)",
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 pt-0.5">
                      {isSelected ? (
                        <CheckCircle2 className="w-5 h-5 text-accent" />
                      ) : (
                        <Circle className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <h4 style={{ fontSize: "var(--text-base)", fontWeight: "var(--font-weight-semibold)" }}>
                          {model.name}
                        </h4>

                        {model.recommended && (
                          <span
                            className="px-2 py-0.5 rounded text-accent border-0"
                            style={{
                              backgroundColor: "var(--alpha-teal-10)",
                              fontSize: "var(--text-sm)",
                              fontWeight: "var(--font-weight-semibold)",
                            }}
                          >
                            Recommended
                          </span>
                        )}

                        {model.isOptional && (
                          <span
                            className="px-2 py-0.5 rounded border"
                            style={{
                              backgroundColor: "var(--muted)",
                              borderColor: "var(--border)",
                              fontSize: "var(--text-sm)",
                              color: "var(--muted-foreground)",
                              fontWeight: "var(--font-weight-semibold)",
                            }}
                          >
                            Optional
                          </span>
                        )}
                      </div>

                      <p className="text-muted-foreground" style={{ fontSize: "var(--text-sm)" }}>
                        {model.provider}
                      </p>

                      {!!model.description && (
                        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: 6 }}>
                          {model.description}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <DialogFooter className="p-6" style={{ borderTop: "1px solid var(--border)" }}>
          <div className="flex items-center justify-between w-full">
            <div>
              <p style={{ fontSize: "var(--text-base)" }}>
                <span className="text-foreground">
                  {selectedModels.length} model{selectedModels.length !== 1 ? "s" : ""} selected
                </span>
              </p>
            </div>

            <button
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 rounded-lg transition-all flex items-center gap-2"
              style={{ backgroundColor: "var(--accent)", color: "var(--accent-foreground)" }}
            >
              <span>Done</span>
              <Check className="w-4 h-4" />
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
